import * as vp from 'vopay';
import { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';

const { Box, Table, SubTitle, formattedCurrency } = vp;

export type TransferOverviewTableProps = {
  autoBalanceTransferResponse: vp.AutoBalanceTransferResponse;
};

type FC = FunctionComponent<TransferOverviewTableProps>;

const TransferOverviewTable: FC = (props: TransferOverviewTableProps) => {
  const { autoBalanceTransferResponse } = props;
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.AutoBalanceTransfer.Overview' });

  const transferDetailPairs = [
    {
      display: t('TransferDetailsTable.Labels.startDate'),
      value: autoBalanceTransferResponse?.ScheduleStartDate || '',
    },
    {
      display: t('TransferDetailsTable.Labels.frequency'),
      value: (
        <span style={{ textTransform: 'capitalize' }}>
          {getParseLabel(autoBalanceTransferResponse?.NameOfFrequency || '')}
        </span>
      ),
    },
    {
      display: t('TransferDetailsTable.Labels.minimumAvailableBalance'),
      value: `$ ${formattedCurrency(autoBalanceTransferResponse?.AutoBalanceTransferAmount || '')}`,
    },
    {
      display: t('TransferDetailsTable.Labels.currency'),
      value: autoBalanceTransferResponse?.Currency || '',
    },
  ];

  const bankAccountDetailsPairs = [
    {
      display: t('BankAccountDetailsTable.Labels.currency'),
      value: autoBalanceTransferResponse?.Currency || '',
      visible: true,
    },
    {
      display: t('BankAccountDetailsTable.Labels.name'),
      value: autoBalanceTransferResponse?.AccountHolderName || '',
      visible: true,
    },
    {
      display: t('BankAccountDetailsTable.Labels.ABARoutingNumber'),
      value: autoBalanceTransferResponse.ABARoutingNumber || '',
      visible: !!autoBalanceTransferResponse.ABARoutingNumber,
    },
    {
      display: t('BankAccountDetailsTable.Labels.financialInstitutionNumber'),
      value: autoBalanceTransferResponse.FinancialInstitutionNumber || '',
      visible: !!autoBalanceTransferResponse.FinancialInstitutionNumber,
    },
    {
      display: t('BankAccountDetailsTable.Labels.branchTransitNumber'),
      value: autoBalanceTransferResponse.BranchTransitNumber || '',
      visible: !!autoBalanceTransferResponse.BranchTransitNumber,
    },
    {
      display: t('BankAccountDetailsTable.Labels.accountNumber'),
      value: autoBalanceTransferResponse.AccountNumber || '',
      visible: true,
    },
  ];

  const TransferTableProps: vp.TableProps = {
    name: 'transfer-details',
    variant: 'pair',
    columns: {
      detailKey: { header: 'transfer-details' },
      detailValue: { header: '' },
    },
    rows: transferDetailPairs.map(({ display, value }) => ({
      detailKey: display,
      detailValue: value,
    })),
  };

  const BankAccountTableProps: vp.TableProps = {
    name: 'bank-account-details',
    variant: 'pair',
    columns: {
      detailKey: { header: 'bank-account-details' },
      detailValue: { header: '' },
    },
    rows: bankAccountDetailsPairs
      .filter((row) => row.visible)
      .map(({ display, value }) => ({
        detailKey: display,
        detailValue: value,
      })),
  };

  return (
    <Box mt="lg" flx="col">
      <Box flx="col" mb="lg">
        <SubTitle subTitle={t('TransferDetailsTable.subTitle')} />
        <Table {...TransferTableProps} />
      </Box>
      <Box flx="col" mb="lg">
        <SubTitle subTitle={t('BankAccountDetailsTable.subTitle')} />
        <Table {...BankAccountTableProps} />
      </Box>
    </Box>
  );
  function getParseLabel(frequency: string): string {
    return frequency === 'biweekly' ? 'Bi-Weekly' : frequency;
  }
};

export default TransferOverviewTable;
